import { useState } from 'react';
import { Box, Typography, Container, Button, Paper, Grid } from '@mui/material';
import { processFile, downloadExcel } from './lib/fileProcessor';

function App() {
  const [status, setStatus] = useState({ message: '', success: false });
  const [previewData, setPreviewData] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState({});

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setStatus({ message: 'Conversion en cours...', success: false });
      
      const { data, workbook, worksheet, buffer } = await processFile(file);
      
      // Création du nom de fichier avec la date
      const today = new Date();
      const datePrefix = today.getFullYear().toString().slice(-2) + 
                        String(today.getMonth() + 1).padStart(2, '0') + 
                        String(today.getDate()).padStart(2, '0');
      const originalName = file.name.replace('.txt', '');
      const fileName = `${datePrefix}_${originalName}.xlsx`;
      
      const { path, publicUrl } = await downloadExcel(workbook, worksheet, fileName, buffer);
      
      setPreviewData({ data, fileName });
      setFileInfo({ workbook, fileName, publicUrl });
      
      // Initialiser les colonnes sélectionnées (colonnes 5 et 6 par défaut)
      const initialColumnSelection = Object.keys(data[0] || {}).reduce((acc, col, index) => {
        acc[col] = index === 4 || index === 5;
        return acc;
      }, {});
      setSelectedColumns(initialColumnSelection);
      
      setStatus({ 
        message: 'Conversion terminée. Le fichier est prêt à être téléchargé.', 
        success: true 
      });
    } catch (error) {
      setStatus({ message: `Erreur: ${error.message}`, success: false });
    }
  };

  const handleDownload = () => {
    if (fileInfo) {
      window.open(fileInfo.publicUrl, '_blank');
    }
  };

  const handleColumnToggle = (columnName) => {
    setSelectedColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  const handleToggleAll = () => {
    const allColumns = Object.keys(previewData.data[0] || {});
    const areAllSelected = allColumns.every(col => selectedColumns[col]);
    
    const newSelection = allColumns.reduce((acc, col) => {
      acc[col] = !areAllSelected;
      return acc;
    }, {});
    
    setSelectedColumns(newSelection);
  };

  const copyColumnData = (columnName) => {
    if (!previewData || !previewData.data) return;
    
    const columnData = previewData.data
      .map(row => row[columnName] || '')
      .join('\n');
    
    navigator.clipboard.writeText(columnData).then(() => {
      const button = document.querySelector(`#copy-${columnName}`);
      if (button) {
        const originalText = button.textContent;
        button.textContent = 'Copié !';
        setTimeout(() => {
          button.textContent = originalText;
        }, 2000);
      }
    });
  };

  const renderColumnData = (data, columnName) => {
    if (!data || data.length === 0) return null;

    return (
      <Paper 
        sx={{ 
          p: 2, 
          m: 1, 
          height: '200px',
          overflow: 'auto',
          backgroundColor: '#f8f9fa',
          '&:hover': {
            backgroundColor: '#f0f0f0',
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: '#f8f9fa',
          zIndex: 1,
          pb: 1,
          borderBottom: '2px solid #1976d2'
        }}>
          <Typography variant="h6">
            {columnName}
          </Typography>
          <Button
            id={`copy-${columnName}`}
            variant="outlined"
            size="small"
            onClick={() => copyColumnData(columnName)}
            sx={{
              minWidth: '100px',
              ml: 1,
              fontSize: '0.75rem'
            }}
          >
            Copier tout
          </Button>
        </Box>
        <Box 
          sx={{ 
            '& > *': { 
              mb: 1,
              p: 1,
              cursor: 'text',
              userSelect: 'text',
              '&:hover': {
                backgroundColor: '#fff',
              }
            }
          }}
        >
          {data.map((row, index) => (
            <Typography 
              key={index} 
              component="div"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                borderBottom: '1px solid #e0e0e0'
              }}
            >
              {row[columnName] || ''}
            </Typography>
          ))}
        </Box>
      </Paper>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4, textAlign: 'center', minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={{
            color: '#1976d2',
            fontWeight: 'bold'
          }}
        >
          Convertisseur TXT vers XLSX
        </Typography>
        
        <Box sx={{ mt: 3 }}>
          <input
            type="file"
            accept=".txt"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="file-input"
          />
          <label htmlFor="file-input">
            <Button 
              variant="contained" 
              component="span"
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#115293'
                },
                padding: '10px 20px',
                fontSize: '1rem'
              }}
            >
              Sélectionner un fichier TXT
            </Button>
          </label>
        </Box>

        {status.message && (
          <Typography 
            color={status.success ? 'success.main' : 'error.main'}
            sx={{ 
              mt: 2,
              padding: '10px',
              backgroundColor: status.success ? '#e8f5e9' : '#ffebee',
              borderRadius: '4px',
              border: `1px solid ${status.success ? '#c8e6c9' : '#ffcdd2'}`
            }}
          >
            {status.message}
          </Typography>
        )}

        {fileInfo && (
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="contained" 
              onClick={handleDownload}
              sx={{
                backgroundColor: '#4caf50',
                '&:hover': {
                  backgroundColor: '#388e3c'
                },
                padding: '10px 20px',
                fontSize: '1rem'
              }}
            >
              Télécharger le fichier XLSX
            </Button>
          </Box>
        )}

        {fileInfo && previewData && (
          <Box 
            sx={{ 
              mt: 3, 
              mb: 2, 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Typography variant="h6" sx={{ color: '#1976d2' }}>
              Sélectionner les colonnes à afficher
            </Typography>
            <Button
              variant="outlined"
              onClick={handleToggleAll}
              sx={{
                mb: 2,
                minWidth: '200px'
              }}
            >
              {Object.values(selectedColumns).every(Boolean) 
                ? 'Tout désélectionner' 
                : 'Tout sélectionner'}
            </Button>
            <Box 
              sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 2,
                justifyContent: 'center',
                maxWidth: '800px'
              }}
            >
              {Object.keys(previewData.data[0] || {}).map((columnName, index) => (
                <Box 
                  key={columnName}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    backgroundColor: selectedColumns[columnName] ? '#e3f2fd' : 'transparent'
                  }}
                >
                  <input
                    type="checkbox"
                    id={`col-${columnName}`}
                    checked={selectedColumns[columnName] || false}
                    onChange={() => handleColumnToggle(columnName)}
                  />
                  <label htmlFor={`col-${columnName}`}>
                    {columnName}
                  </label>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {previewData && (
          <Box sx={{ mt: 4, width: '100%' }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{
                color: '#1976d2',
                marginBottom: '1rem'
              }}
            >
              Aperçu des données
            </Typography>
            <Grid container spacing={2}>
              {Object.keys(previewData.data[0] || {})
                .filter(columnName => selectedColumns[columnName])
                .map((columnName) => (
                  <Grid item xs={12} sm={6} md={4} key={columnName}>
                    {renderColumnData(previewData.data, columnName)}
                  </Grid>
                ))}
            </Grid>
          </Box>
        )}
      </Box>
    </Container>
  );
}

export default App;